import Scrollbars from "react-custom-scrollbars-2";
import { useCallback, useContext, useRef } from "react";
import { useIntegrationTransactionQuery } from "../../api/integrationTransaction.api";
import {
    FinancialDocument,
    ImporterErrors,
} from "../../common/types/financialDocument";
import { IntegrationAccount } from "../../common/types/integrationAccount";
import {
    GridTable,
    GridTableHeader,
} from "../general/Tables/GridTable/GridTable";
import { useFinancialDocumentReviewMutation } from "../../api/financialDocument.api";
import styles from "./ImporterPage.module.scss";
import { isTransactionsProcessingFinished } from "./helpers";
import { ImporterTransactionRow } from "./ImporterTransactionRow";
import { ImporterBalanceSummary } from "./ImporterBalanceSummary";
import { ImporterContext } from "./importer.context";

interface Props {
    documentRowId: string;
    financialDocument: FinancialDocument;
    integrationAccount: IntegrationAccount;
}

export const ImporterDataPreview: React.FC<Props> = ({
    documentRowId,
    financialDocument,
    integrationAccount,
}) => {
    const { updateDocumentRow } = useContext(ImporterContext);
    const reviewed = useRef(false);
    const transactions = useIntegrationTransactionQuery(integrationAccount.id, {
        enabled: Boolean(financialDocument.numTransactions),
    });
    const onReviewed = useCallback(
        (document: FinancialDocument) => {
            updateDocumentRow(documentRowId, {
                financialDocument: document,
            });
        },
        [documentRowId, updateDocumentRow],
    );
    const reviewMutation = useFinancialDocumentReviewMutation(
        financialDocument,
        onReviewed,
    );

    if (!isTransactionsProcessingFinished(financialDocument)) {
        return (
            <p className={styles.transactionsMessage}>
                We're extracting data from this document. Please wait...
            </p>
        );
    }

    if (!financialDocument.numTransactions) {
        return (
            <p className={styles.transactionsMessage}>
                No transactions found in this document
            </p>
        );
    }

    if (!transactions.data) {
        return null;
    }

    if (
        financialDocument.importerErrors?.includes(
            ImporterErrors.NOT_CONFIDENT,
        ) &&
        !reviewed.current
    ) {
        reviewed.current = true;
        reviewMutation.mutate();
    }

    return (
        <Scrollbars>
            <ImporterBalanceSummary
                documentRowId={documentRowId}
                financialDocument={financialDocument}
                transactions={transactions.data}
            />
            <GridTable className={styles.transactionsTable} noBorder>
                <GridTableHeader columnBorders>
                    <div>Date</div>
                    <div>Description</div>
                    <div className={styles.amountHeader}>Amount</div>
                </GridTableHeader>
                {transactions.data.map((transaction) => (
                    <ImporterTransactionRow
                        key={transaction.id}
                        transaction={transaction}
                    />
                ))}
            </GridTable>
        </Scrollbars>
    );
};
